import { MAP_FACINGS, OPEN_TENT_SPAN, STICKERS, WALL_KINDS, buildingGroups, cellKey, edgeKey, objectFacing, objectGroups, stickerArtId, trailStep } from '../../../shared/mapLayout.mjs';
import MAP_ART from '../../../shared/mapArtCatalog.json';

// What a layered map draws over its painted ground: daytime shade, roofs, stamps and walls, each exactly on its squares.
// Each stamp is clipped to its patch, so it stays on the squares it was placed on. Only looks reach past them: shade, the
// soft fringe of dense art, and magic circles. Art comes from mapArtCatalog.json (built by server/scripts/build-map-art-catalog.js); anything the catalog
// has no art for, or whose image won't load, is drawn in code by OBJECT_ART and the roof and wall fallbacks.
const SNOWY_GROUND = new Set(['snow', 'ice']);
// How a stamp's art may vary: natural things turn to any angle and flip, built things turn by quarters so they stay
// square to the grid, and the rest stay as drawn.
const FREE_TURN = new Set(['tree', 'brush', 'rubble', 'fog', 'blood-puddle', 'bodies', 'magic-circle-blue', 'magic-circle-green', 'magic-circle-purple', 'magic-circle-red']);
const QUARTER_TURN = new Set(['boulder', 'campfire', 'torch', 'wall-light', 'lever-wall', 'lever-floor', 'chest', 'safe', 'wall-safe', 'strong-box', 'furniture', 'pillar', 'foliage']);
// Art that fills its whole square rather than fitting inside it.
const FILLS_SQUARE = new Set(['foliage', 'solid', 'stairs']);
const BLOOD_STAMPS = Object.freeze(['blood-puddle', 'blood-footprints']);
const CLOSED_TENTS = Object.freeze(['tent-brown', 'tent-canvas', 'tent-red']);
const TENT_CENTER = 'tent-center';
// How much of FA's stairs art is its baked shadow along the bottom and its dark line down each side, as shares of the image.
const STAIR_EDGES = Object.freeze({ shadow: 0.09, side: 0.01 });
// Stamps whose touching squares read as one line or mass: a further piece of art covers each join.
const BLENDED_STAMPS = new Set(['brush', 'rubble', 'foliage', 'fog']);
const JOIN_SIZE = 0.8;
// Brush thins out where it touches more brush: this share of a patch's pieces goes, from the joins.
const THINNED_STAMPS = Object.freeze({ brush: 0.2 });
// Art that must read as a solid mass: pieces drawn this much larger, over dark shade if it has one, so no ground shows
// between them. They aren't clipped, so a soft fringe spills past the patch instead of a hard edge.
const DENSE_STAMPS = Object.freeze({ foliage: { size: 1.3, shade: '#1e2e16' }, fog: { size: 1.7 } });
// Art a stamp may also use when none of its own kind touches it, corners included: stumps among the brush.
const LONE_ART = Object.freeze({ brush: 'brush-lone' });
const AROUND = [[-1, -1], [0, -1], [1, -1], [-1, 0], [1, 0], [-1, 1], [0, 1], [1, 1]];
// Crates sit at loose angles, and a full 2x2 of them becomes a pile of mixed sizes. Each pile is a set of boxes in the two
// squares, [left, top, width, height], none overlapping, so no crate ever lies over another.
const CRATE_ANGLE = Object.freeze({ lone: 0.7, piled: 0.3 });
// Beside a wall, crates line up with it instead: a lone one in a box this deep, this far toward the wall.
const CRATE_ON_WALL = Object.freeze({ depth: 0.76, toward: 0.06 });
const CRATE_PILES = Object.freeze([
  [[0, 0, 1.2, 1.2], [1.2, 0, 0.8, 0.6], [1.2, 0.6, 0.8, 0.6], [0, 1.2, 0.67, 0.8], [0.67, 1.2, 0.67, 0.8], [1.34, 1.2, 0.66, 0.8]],
  [[0.8, 0, 1.2, 1.2], [0, 0, 0.8, 0.6], [0, 0.6, 0.8, 0.6], [0, 1.2, 1, 0.8], [1, 1.2, 1, 0.8]],
  [[0, 0, 0.67, 0.85], [0.67, 0, 0.67, 0.85], [1.34, 0, 0.66, 0.85], [0, 0.85, 1.15, 1.15], [1.15, 0.85, 0.85, 0.57], [1.15, 1.42, 0.85, 0.58]],
  [[0.85, 0.8, 1.15, 1.2], [0, 0, 0.85, 0.8], [0.85, 0, 0.6, 0.8], [1.45, 0, 0.55, 0.8], [0, 0.8, 0.85, 0.6], [0, 1.4, 0.85, 0.6]],
]);
// Two touching squares of bed make one bed. Sizes are how long it's drawn, in squares; on one lone square it's a small one.
const BED_SIZE = Object.freeze({ full: 1.64, lone: 0.95 });
// Tables the same way: an empty one across two squares, a filled one across a 2x2.
const TABLE_SIZE = Object.freeze({ empty: 1.52, filled: 1.88, lone: 0.95 });
// A tent fills the squares it uses, a little bigger than FA's 3x3 draws it.
const TENT_SIZE = Object.freeze({ full: 3, lone: 0.95 });
// Where a torch goes in an open tent, in squares from the middle of its square. Beside the tent's middle it comes in from the
// square's edge to stand on the fabric. A corner square is mostly outside the round tent, and it goes further out, clear of it.
const TENT_TORCH = Object.freeze({ onFabric: -0.18, outside: 0.2 });
// The fabric of an open tent, which stops light: how far out it stands in squares, in how many straight pieces. The art's
// door is this far round from east, clockwise, and this wide, a little wider than drawn so soft shadows don't close it.
const TENT_WALL = Object.freeze({ radius: 1.28, pieces: 24, doorAngle: (66 * Math.PI) / 180, doorWidth: (16 * Math.PI) / 180 });
// Long, narrow filled tables, like those with two chairs, sit diagonally across their 2x2, a little smaller to stay in it.
const NARROW_TABLE = Object.freeze({ ratio: 1.4, size: 1.75 });
// Stamps drawn against a wall on their square's side: how long the art is anywhere, how far it moves toward the wall, and
// how far along it when a second wall makes a corner, all in squares. The foot of the art, the bottom of the image, meets
// the wall, and stays clear of thick walls, which are drawn over it. `footTurn` is for art whose foot is on another side.
// `flush` art instead sits with its foot on the face of the wall as drawn, however thick its art; `toward` is for when
// the art isn't known.
const WALL_HUGS = Object.freeze({
  torch: { size: 0.6, toward: 0.08, along: 0.2 },
  'wall-light': { size: 0.36, toward: 0.27, along: 0.2, footTurn: Math.PI, flush: true },
  'lever-wall': { size: 0.32, toward: 0.18, along: 0.2, footTurn: -Math.PI / 2 },
  'wall-safe': { size: 0.61, toward: 0.24, along: 0.2, footTurn: Math.PI },
  chest: { size: 0.85, toward: 0.1, along: 0.04, footTurn: Math.PI },
  safe: { size: 0.85, toward: 0.03, along: 0.08, footTurn: Math.PI },
  'strong-box': { size: 0.34, toward: 0.2, along: 0.2 },
});
// Small art drawn at its true size, not filling its square: how long it is, in squares.
const SMALL_ART = Object.freeze({ 'lever-floor': 0.64 });
// Art drawn past its square so its detail survives being scaled down: how wide it is, in squares. It isn't cut to the square.
const LARGE_ART = Object.freeze({ tree: 1.8 });
const WALLS_HUGGED = new Set(['wall', 'low-wall']);
// A tile of roof art covers this many squares each way, so its pattern isn't repeated per square.
const ROOF_TILE_SQUARES = 3;
// Stamps that shade the ground around them. The shade is only a look: no rule reads it.
const SHADE_STAMPS = new Set(['tree']);
const SHADE = Object.freeze({ reach: 1.55, drift: 0.22, strength: 0.41 });
// A tree top covers this many squares each way, around its tree. Tree tops aren't drawn into the map picture: the battle
// map lays them over the tokens, as a separate layer (MapCanopy). They are only a look: no rule reads them.
const TREE_TOP_SQUARES = 3;
// Stamps that light the squares around them on a night map: how many squares out, and how strongly. Magic circles glow
// in their own color, day or night, 2 squares past their ring. The light is only a look: no rule reads it.
const circleGlow = (color) => ({ reachPast: 2, strength: 1, color, always: true });
const LIGHT_STAMPS = Object.freeze({
  campfire: { reach: 6, strength: 1 },
  torch: { reach: 4, strength: 0.75 },
  'wall-light': { reach: 6, strength: 0.75 },
  'magic-circle-blue': circleGlow([108, 199, 218]),
  'magic-circle-green': circleGlow([122, 222, 32]),
  'magic-circle-purple': circleGlow([191, 58, 246]),
  'magic-circle-red': circleGlow([220, 8, 20]),
});
// Art lying flat on the ground, as wide as the stamp's size, from its stamped square to the right and down. It goes under
// other stamps, with no shadow, and it's the one stamp art that may leave its square. No rule reads it.
const GROUND_ART = new Set(['magic-circle-blue', 'magic-circle-green', 'magic-circle-purple', 'magic-circle-red', 'blood-puddle', 'blood-footprints']);
// How much of the way to a diagonal neighbour a footprint stretches: fully would be half as long again, which looks wrong.
const TRAIL_STRETCH = 0.6;
// A trail crosses little of a corner square, so the print there loses its last step, `notch` being where the part it's cut
// from starts, as shares of the image from its top left. `inward` moves it toward the inside of the turn, where the trail
// runs, and `forward` is how far its remaining steps sit from the image's middle, all in squares.
const TRAIL_CORNER = Object.freeze({ notch: [0.5, 0.7], inward: 0.17, forward: 0.12 });
// How much of the light each thing stops, and how much of its square casts the shadow. Windows let it through.
const LIGHT_BLOCKS = Object.freeze({
  solid: { block: 1, size: 1 },
  ...Object.fromEntries([...CLOSED_TENTS, TENT_CENTER].map((id) => [id, { block: 1, size: 1 }])),
  tree: { block: 0.6, size: 0.6 },
  pillar: { block: 0.6, size: 0.6 },
  foliage: { block: 0.6, size: 1 },
  boulder: { block: 0.35, size: 0.8 },
  crates: { block: 0.35, size: 0.8 },
  brush: { block: 0.2, size: 1 },
});
const WALL_LIGHT_BLOCKS = Object.freeze({ wall: 1, door: 1, 'low-wall': 0.35 });
const NIGHT_TINT = 'rgba(15, 23, 42, 0.55)';
// The finished map. The generator's ground is 1024 pixels, about 43 to a square on 24 squares, too few to keep a stamp's
// detail, so it's scaled up to this and stamps are drawn at the larger size. WebP keeps it inside the server's upload limit.
const MAP_OUTPUT = Object.freeze({ side: 2048, type: 'image/webp', quality: 0.92 });
// How many rows of smoke art make up the players' fog texture.
const SMOKE_ROWS = 4;

// A repeatable 0..1 value for a square, so one layout always draws the same way.
const wobble = (x, y, salt) => {
  const value = Math.sin(x * 12.9898 + y * 78.233 + salt * 37.719) * 43758.5453;
  return value - Math.floor(value);
};

const softShadow = (ctx, { cx, cy, unit }, radius, drop = 0.05) => {
  const y = cy + unit * drop;
  const gradient = ctx.createRadialGradient(cx, y, unit * radius * 0.3, cx, y, unit * radius);
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0.5)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(cx, y, unit * radius, 0, Math.PI * 2);
  ctx.fill();
};

const blob = (ctx, x, y, radius, color) => {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fill();
};

// Leafy clumps spread over a square, darkest first.
const clumps = (ctx, square, { count, size, spread, colors }) => {
  const { cx, cy, unit, x, y } = square;
  colors.forEach((color, layer) => {
    for (let i = 0; i < count; i += 1) {
      const angle = wobble(x, y, i + layer * 11) * Math.PI * 2;
      const reach = unit * spread * Math.sqrt(wobble(x, y, i + layer * 17 + 5)) * (1 - layer * 0.18);
      blob(ctx, cx + Math.cos(angle) * reach, cy + Math.sin(angle) * reach, unit * size * (1 - layer * 0.2), color);
    }
  });
};

// Snow settled on a crown: small patches scattered over it, with the green still showing between them.
const frost = (ctx, { cx, cy, unit, x, y }, { count, size, spread }) => {
  for (let i = 0; i < count; i += 1) {
    const angle = wobble(x, y, i + 90) * Math.PI * 2;
    const reach = unit * spread * Math.sqrt(wobble(x, y, i + 120));
    blob(ctx, cx + Math.cos(angle) * reach, cy + Math.sin(angle) * reach, unit * size * (0.6 + wobble(x, y, i + 150) * 0.6), '#f7fbfd');
  }
};

const box = (ctx, x, y, size, fill, line) => {
  ctx.fillStyle = fill;
  ctx.fillRect(x, y, size, size);
  ctx.strokeStyle = line;
  ctx.lineWidth = size * 0.08;
  ctx.strokeRect(x, y, size, size);
  ctx.beginPath();
  ctx.moveTo(x, y); ctx.lineTo(x + size, y + size);
  ctx.moveTo(x + size, y); ctx.lineTo(x, y + size);
  ctx.stroke();
};

const container = (color, halfWidth, halfHeight) => (ctx, { cx, cy, unit }) => {
  ctx.fillStyle = color;
  ctx.fillRect(cx - unit * halfWidth, cy - unit * halfHeight, unit * halfWidth * 2, unit * halfHeight * 2);
  ctx.strokeStyle = '#1f1a16';
  ctx.lineWidth = unit * 0.03;
  ctx.strokeRect(cx - unit * halfWidth, cy - unit * halfHeight, unit * halfWidth * 2, unit * halfHeight * 2);
};

const lever = (length) => (ctx, { cx, cy, unit }) => {
  ctx.fillStyle = '#3f3d3a';
  ctx.fillRect(cx - unit * 0.1, cy - unit * length, unit * 0.2, unit * length * 2);
  blob(ctx, cx, cy - unit * length, unit * 0.08, '#b98a4a');
};

const magicCircle = (color) => (ctx, { cx, cy, unit }) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = unit * 0.05;
  for (const radius of [0.44, 0.3]) {
    ctx.beginPath();
    ctx.arc(cx, cy, unit * radius, 0, Math.PI * 2);
    ctx.stroke();
  }
};

const OBJECT_ART = {
  tree: (ctx, square) => {
    softShadow(ctx, square, 0.47, 0.03);
    const colors = square.snowy ? ['#24483a', '#335f4c', '#4b7a64'] : ['#1f4d22', '#2f6b2c', '#4b8c39', '#74ad4c'];
    blob(ctx, square.cx, square.cy, square.unit * 0.38, colors[0]);
    clumps(ctx, square, { count: 7, size: 0.15, spread: 0.26, colors });
    if (square.snowy) frost(ctx, square, { count: 11, size: 0.06, spread: 0.32 });
  },
  brush: (ctx, square) => {
    softShadow(ctx, square, 0.45, 0.02);
    clumps(ctx, square, { count: 9, size: 0.13, spread: 0.32, colors: ['#165f58', '#1f8a7c', '#3fb3a0'] });
    if (square.snowy) frost(ctx, square, { count: 7, size: 0.05, spread: 0.36 });
  },
  foliage: (ctx, square) => {
    const { x0, y0, width, height } = square;
    ctx.fillStyle = '#143f1d';
    ctx.fillRect(x0, y0, width, height);
    clumps(ctx, square, { count: 12, size: 0.17, spread: 0.46, colors: ['#1d5a2b', '#2f7a36', '#4c9a44'] });
    if (square.snowy) frost(ctx, square, { count: 10, size: 0.06, spread: 0.46 });
  },
  crates: (ctx, square) => {
    const { x0, y0, unit } = square;
    softShadow(ctx, square, 0.46, 0.03);
    box(ctx, x0 + unit * 0.14, y0 + unit * 0.14, unit * 0.4, '#a9743e', '#5c3a1b');
    box(ctx, x0 + unit * 0.5, y0 + unit * 0.48, unit * 0.34, '#94632f', '#5c3a1b');
    blob(ctx, x0 + unit * 0.68, y0 + unit * 0.3, unit * 0.15, '#7a4c24');
    blob(ctx, x0 + unit * 0.68, y0 + unit * 0.3, unit * 0.1, '#a9743e');
    blob(ctx, x0 + unit * 0.3, y0 + unit * 0.72, unit * 0.14, '#7a4c24');
    blob(ctx, x0 + unit * 0.3, y0 + unit * 0.72, unit * 0.09, '#a9743e');
  },
  boulder: (ctx, square) => {
    softShadow(ctx, square, 0.47, 0.04);
    blob(ctx, square.cx, square.cy, square.unit * 0.36, '#5f6263');
    clumps(ctx, square, { count: 5, size: 0.14, spread: 0.2, colors: ['#74787a', '#90959a'] });
  },
  furniture: (ctx, square) => {
    const { x0, y0, unit } = square;
    softShadow(ctx, square, 0.47, 0.03);
    ctx.fillStyle = '#8a5a30';
    ctx.fillRect(x0 + unit * 0.12, y0 + unit * 0.2, unit * 0.76, unit * 0.6);
    ctx.strokeStyle = '#4f3218';
    ctx.lineWidth = unit * 0.03;
    ctx.strokeRect(x0 + unit * 0.12, y0 + unit * 0.2, unit * 0.76, unit * 0.6);
    for (const row of [0.4, 0.6]) {
      ctx.beginPath();
      ctx.moveTo(x0 + unit * 0.12, y0 + unit * row);
      ctx.lineTo(x0 + unit * 0.88, y0 + unit * row);
      ctx.stroke();
    }
  },
  pillar: (ctx, square) => {
    softShadow(ctx, square, 0.47, 0.03);
    blob(ctx, square.cx, square.cy, square.unit * 0.36, '#74787a');
    blob(ctx, square.cx, square.cy, square.unit * 0.27, '#a3a6a8');
  },
  solid: (ctx, { x0, y0, width, height }) => {
    ctx.fillStyle = '#3f3d3a';
    ctx.fillRect(x0, y0, width, height);
  },
  bodies: (ctx, square) => {
    const { cx, cy, unit } = square;
    blob(ctx, cx, cy, unit * 0.3, '#5a1f1a');
    blob(ctx, cx, cy - unit * 0.22, unit * 0.1, '#8a7a68');
    blob(ctx, cx, cy + unit * 0.02, unit * 0.16, '#6f6252');
  },
  rubble: (ctx, square) => {
    const { x0, y0, unit, x, y } = square;
    softShadow(ctx, square, 0.45, 0.02);
    for (let i = 0; i < 14; i += 1) {
      blob(ctx, x0 + unit * (0.1 + wobble(x, y, i) * 0.8), y0 + unit * (0.1 + wobble(x, y, i + 40) * 0.8), unit * (0.04 + wobble(x, y, i + 80) * 0.07), i % 3 ? '#7b7770' : '#9b968d');
    }
  },
  campfire: (ctx, square) => {
    const { cx, cy, unit } = square;
    softShadow(ctx, square, 0.42, 0.02);
    blob(ctx, cx, cy, unit * 0.32, '#2b1d14');
    blob(ctx, cx, cy, unit * 0.22, '#e8701a');
    blob(ctx, cx, cy, unit * 0.11, '#ffd166');
  },
  torch: (ctx, square) => {
    const { cx, cy, unit } = square;
    softShadow(ctx, square, 0.25, 0.02);
    blob(ctx, cx, cy, unit * 0.14, '#3a2717');
    blob(ctx, cx, cy, unit * 0.09, '#e8701a');
    blob(ctx, cx, cy, unit * 0.05, '#ffd166');
  },
  bed: container('#6b4a2c', 0.3, 0.42),
  'table-empty': container('#6b4a2c', 0.42, 0.3),
  'table-filled': container('#7a5a35', 0.42, 0.42),
  chest: container('#7a4f2a', 0.42, 0.26),
  safe: container('#3a3d42', 0.36, 0.36),
  'wall-safe': container('#3a3d42', 0.3, 0.1),
  'wall-light': (ctx, square) => {
    container('#26231f', 0.1, 0.1)(ctx, square);
    blob(ctx, square.cx, square.cy, square.unit * 0.06, '#ffd166');
  },
  'strong-box': container('#5d6168', 0.16, 0.13),
  'lever-wall': lever(0.26),
  'lever-floor': lever(0.34),
  'magic-circle-blue': magicCircle('rgb(108, 199, 218)'),
  'magic-circle-green': magicCircle('rgb(122, 222, 32)'),
  'magic-circle-purple': magicCircle('rgb(191, 58, 246)'),
  'magic-circle-red': magicCircle('rgb(220, 8, 20)'),
  ...Object.fromEntries(BLOOD_STAMPS.map((id) => [id, (ctx, { cx, cy, unit }) => {
    ctx.fillStyle = 'rgba(92, 14, 14, 0.7)';
    ctx.beginPath();
    ctx.arc(cx, cy, unit * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }])),
  ...Object.fromEntries([...CLOSED_TENTS, TENT_CENTER].map((id) => [id, (ctx, { x0, y0, width, height }) => {
    ctx.fillStyle = id === TENT_CENTER ? '#4a3826' : '#8a6a44';
    ctx.fillRect(x0, y0, width, height);
  }])),
  stairs: (ctx, { cx, cy, width, height, turn }) => {
    ctx.translate(cx, cy);
    ctx.rotate(turn);
    for (let step = 0; step < 5; step += 1) {
      ctx.fillStyle = `rgb(${130 - step * 14}, ${124 - step * 14}, ${112 - step * 14})`;
      ctx.fillRect(-width / 2, -height / 2 + (height * step) / 5, width, height / 5);
    }
    ctx.rotate(-turn);
    ctx.translate(-cx, -cy);
  },
  fog: (ctx, { cx, cy, unit }) => {
    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, unit * 0.75);
    gradient.addColorStop(0, 'rgba(226, 232, 240, 0.8)');
    gradient.addColorStop(1, 'rgba(226, 232, 240, 0.35)');
    ctx.fillStyle = gradient;
    ctx.fillRect(cx - unit, cy - unit, unit * 2, unit * 2);
  },
};

const WALL_ART = {
  wall: { color: '#4d4843', width: 0.28 },
  'low-wall': { color: '#7a6a58', width: 0.14 },
  window: { color: '#8fd3ea', width: 0.2 },
  door: { color: '#7a4a20', width: 0.3 },
};

const squareRects = (ctx, cells, cw, ch) => {
  ctx.beginPath();
  for (const { x, y } of cells) ctx.rect(x * cw, y * ch, cw, ch);
};

// Shade pooled on the ground around a tree, drifting away from the light at the top left. It goes down first, so it
// falls on the ground and never on a roof, a stamp or a wall. There is no sun to cast it at night.
const drawShade = (ctx, layout, cw, ch, unit) => {
  if (layout.lighting === 'night') return;
  for (const group of objectGroups(layout)) {
    if (!SHADE_STAMPS.has(group.id)) continue;
    for (const { x, y } of group.cells) {
      const cx = (x + 0.5 + SHADE.drift) * cw;
      const cy = (y + 0.5 + SHADE.drift) * ch;
      const radius = unit * SHADE.reach;
      const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
      gradient.addColorStop(0, `rgba(14, 22, 16, ${SHADE.strength})`);
      gradient.addColorStop(1, 'rgba(14, 22, 16, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
    }
  }
};

// The art for a key, by ground: snow art on snow when there is any, otherwise the plain art.
// Art a stamp may also need: a large puddle, and the worn print at the end of a trail.
const ART_WITH = Object.freeze({ 'blood-puddle': 'blood-puddle-large', 'blood-footprints': 'blood-footprints-end' });
// Retired stamps that draw with newer art, on maps that still have them.
const ART_STAND_INS = Object.freeze({ furniture: 'table-empty' });

const artList = (art, id, snowy) => {
  const set = art?.[id] || art?.[ART_STAND_INS[id]];
  return (snowy && set?.snow?.length ? set.snow : set?.plain) || [];
};

const naturalSize = (image) => [image.naturalWidth || image.width || 1, image.naturalHeight || image.height || 1];

// One piece of art centered on (cx, cy), its longer side `size` across, with a soft shadow that follows its shape.
// How to fit art in a box at a loose angle: its long side along the box's, and small enough that its corners stay inside.
// Round art, drawn in a square image, needs no room for corners.
const fitInBox = (width, height, [boxWidth, boxHeight], angle) => {
  if (Math.abs(width - height) < Math.max(width, height) * 0.05) return { turn: angle, scale: Math.min(boxWidth, boxHeight) / Math.max(width, height) };
  const turn = (width >= height) === (boxWidth >= boxHeight) ? angle : angle + Math.PI / 2;
  const [cos, sin] = [Math.abs(Math.cos(turn)), Math.abs(Math.sin(turn))];
  return { turn, scale: Math.min(boxWidth / (width * cos + height * sin), boxHeight / (width * sin + height * cos)) };
};

const drawSprite = (ctx, image, { cx, cy, size, unit, turn: angle = 0, flip = false, fill = false, shadow = true, box = null, crop = null, notch = null }) => {
  const [width, height] = naturalSize(image);
  const fitted = box && fitInBox(width, height, box, angle);
  const turn = fitted ? fitted.turn : angle;
  const scale = fitted ? fitted.scale : size / Math.max(width, height);
  const drawWidth = fill ? size : width * scale;
  const drawHeight = fill ? size : height * scale;
  ctx.save();
  if (shadow) {
    ctx.shadowColor = 'rgba(0, 0, 0, 0.45)';
    ctx.shadowBlur = unit * 0.12;
    ctx.shadowOffsetX = unit * 0.03;
    ctx.shadowOffsetY = unit * 0.03;
  }
  ctx.translate(cx, cy);
  ctx.rotate(turn);
  if (flip) ctx.scale(-1, 1);
  if (notch) {
    const [left, top] = [-drawWidth / 2 + notch[0] * drawWidth, -drawHeight / 2 + notch[1] * drawHeight];
    ctx.beginPath();
    ctx.moveTo(-drawWidth / 2, -drawHeight / 2);
    [[drawWidth / 2, -drawHeight / 2], [drawWidth / 2, top], [left, top], [left, drawHeight / 2], [-drawWidth / 2, drawHeight / 2]].forEach(([x, y]) => ctx.lineTo(x, y));
    ctx.closePath();
    ctx.clip();
  }
  if (crop) {
    const [left, right] = [crop.left * width, crop.right * width];
    ctx.drawImage(image, left, 0, width - left - right, Math.floor(height * (1 - crop.bottom)), -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  } else ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  ctx.restore();
};

// Each slope of a roof: which way it faces, how to turn the tile, whose courses run up and down, to lie along that edge, and
// a touch of light or shade, as the light comes from the upper left.
const ROOF_FACES = Object.freeze([
  { face: 1, turn: 0, shade: 'rgba(255, 255, 255, 0.06)' },
  { face: 2, turn: Math.PI, shade: 'rgba(0, 0, 0, 0.16)' },
  { face: 3, turn: Math.PI / 2, shade: 'rgba(255, 255, 255, 0.06)' },
  { face: 4, turn: -Math.PI / 2, shade: 'rgba(0, 0, 0, 0.16)' },
]);
const ROOF_RIDGE = Object.freeze({ width: 0.035, color: [22, 22, 26, 225] });

/**
 * Which way a roof slopes at each spot, `pixels` to the square: 1 west, 2 east, 3 north, 4 south, 0 off the roof. Each spot
 * slopes toward its nearest edge, so ridges and corners fall where the slopes meet, for any painted shape.
 */
export const roofSlopes = (cells, pixels) => {
  const xs = cells.map(({ x }) => x);
  const ys = cells.map(({ y }) => y);
  const [left, top] = [Math.min(...xs), Math.min(...ys)];
  const width = (Math.max(...xs) - left + 1) * pixels;
  const height = (Math.max(...ys) - top + 1) * pixels;
  const inside = new Uint8Array(width * height);
  for (const { x, y } of cells) {
    for (let row = 0; row < pixels; row += 1) inside.fill(1, ((y - top) * pixels + row) * width + (x - left) * pixels, ((y - top) * pixels + row) * width + (x - left + 1) * pixels);
  }
  // Spreading in from the outside, each spot learns the nearest spot beyond the roof, one ring at a time, diagonals counting
  // as one step. That keeps corners on the diagonal, and turns the slopes round inner corners as valleys.
  const fromX = new Int32Array(width * height);
  const fromY = new Int32Array(width * height);
  const reached = new Uint8Array(width * height);
  const isInside = (x, y) => x >= 0 && y >= 0 && x < width && y < height && inside[y * width + x];
  const queue = new Int32Array(width * height);
  let tail = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!inside[y * width + x]) continue;
      const open = [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]].find(([a, b]) => !isInside(a, b));
      if (!open) continue;
      const i = y * width + x;
      [fromX[i], fromY[i]] = open;
      reached[i] = 1;
      queue[tail] = i;
      tail += 1;
    }
  }
  for (let head = 0; head < tail; head += 1) {
    const i = queue[head];
    const [x, y] = [i % width, Math.floor(i / width)];
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        if (!isInside(x + dx, y + dy)) continue;
        const next = (y + dy) * width + x + dx;
        if (reached[next]) continue;
        reached[next] = 1;
        fromX[next] = fromX[i];
        fromY[next] = fromY[i];
        queue[tail] = next;
        tail += 1;
      }
    }
  }
  const slopes = new Uint8Array(width * height);
  for (let i = 0; i < slopes.length; i += 1) {
    if (!inside[i]) continue;
    const across = i % width - fromX[i];
    const down = Math.floor(i / width) - fromY[i];
    if (Math.abs(across) > Math.abs(down)) slopes[i] = across > 0 ? 1 : 2;
    else slopes[i] = down > 0 ? 3 : 4;
  }
  return { slopes, left, top, width, height };
};

// A building's roof: the tile laid along each slope, with a line where slopes meet. It's only a look, as a flat roof was.
const drawRoofSlopes = (ctx, building, tile, cw, ch, unit) => {
  const pixels = Math.max(8, Math.round(unit));
  const { slopes, left, top, width, height } = roofSlopes(building.cells, pixels);
  const layer = newCanvas(width, height);
  const mask = newCanvas(width, height);
  const side = pixels * ROOF_TILE_SQUARES;
  const reach = Math.ceil(Math.hypot(width, height) / 2 / side) + 1;
  const lay = () => ctx.drawImage(layer.canvas, left * cw, top * ch, (width / pixels) * cw, (height / pixels) * ch);
  for (const { face, turn, shade } of ROOF_FACES) {
    const cut = mask.ctx.createImageData(width, height);
    for (let i = 0; i < slopes.length; i += 1) if (slopes[i] === face) cut.data[i * 4 + 3] = 255;
    mask.ctx.putImageData(cut, 0, 0);
    layer.ctx.globalCompositeOperation = 'source-over';
    layer.ctx.clearRect(0, 0, width, height);
    layer.ctx.save();
    layer.ctx.translate(width / 2, height / 2);
    layer.ctx.rotate(turn);
    for (let row = -reach; row < reach; row += 1) for (let col = -reach; col < reach; col += 1) layer.ctx.drawImage(tile, col * side, row * side, side, side);
    layer.ctx.restore();
    layer.ctx.fillStyle = shade;
    layer.ctx.fillRect(0, 0, width, height);
    layer.ctx.globalCompositeOperation = 'destination-in';
    layer.ctx.drawImage(mask.canvas, 0, 0);
    lay();
  }
  const ridge = layer.ctx.createImageData(width, height);
  const line = Math.max(1, Math.round(pixels * ROOF_RIDGE.width));
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const here = slopes[y * width + x];
      const beside = x + line < width ? slopes[y * width + x + line] : 0;
      const below = y + line < height ? slopes[(y + line) * width + x] : 0;
      if (here && ((beside && beside !== here) || (below && below !== here))) ridge.data.set(ROOF_RIDGE.color, (y * width + x) * 4);
    }
  }
  layer.ctx.globalCompositeOperation = 'source-over';
  layer.ctx.clearRect(0, 0, width, height);
  layer.ctx.putImageData(ridge, 0, 0);
  lay();
};

const drawRoofs = (ctx, layout, cw, ch, unit, art) => {
  for (const building of buildingGroups(layout)) {
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
    ctx.shadowBlur = unit * 0.35;
    ctx.fillStyle = '#6d4436';
    squareRects(ctx, building.cells, cw, ch);
    ctx.fill();
    ctx.restore();
    ctx.save();
    squareRects(ctx, building.cells, cw, ch);
    ctx.clip();
    const tiles = artList(art, 'roof', false);
    if (tiles.length) {
      // One roofing per building.
      drawRoofSlopes(ctx, building, tiles[Math.floor(wobble(building.left, building.top, 1) * tiles.length)], cw, ch, unit);
      ctx.restore();
      continue;
    }
    ctx.strokeStyle = 'rgba(40, 22, 16, 0.55)';
    ctx.lineWidth = unit * 0.03;
    ctx.beginPath();
    for (const { x, y } of building.cells) {
      for (let course = 0; course < 3; course += 1) {
        const top = (y + course / 3) * ch;
        ctx.moveTo(x * cw, top);
        ctx.lineTo((x + 1) * cw, top);
        for (let shingle = course % 2 ? 0.25 : 0; shingle < 1; shingle += 0.5) {
          ctx.moveTo((x + shingle) * cw, top);
          ctx.lineTo((x + shingle) * cw, top + ch / 3);
        }
      }
    }
    ctx.stroke();
    ctx.restore();
  }
};

// Where ground art stamped on a square lies, in squares, kept on the map.
const groundSpan = (layout, x, y) => {
  const size = layout.objectSizes?.[cellKey(x, y)] || 1;
  return { size, x: Math.min(x, layout.columns - size) + size / 2, y: Math.min(y, layout.rows - size) + size / 2 };
};

// How a stamp sits against a wall on its square's side, or null if it has none. South first, so a torch between two walls
// takes one rather than staying between them.
const wallSides = (layout, x, y) => {
  const by = (key) => WALLS_HUGGED.has(layout.walls[key]);
  return { south: by(edgeKey(x, y + 1, 'h')), west: by(edgeKey(x, y, 'v')), north: by(edgeKey(x, y, 'h')), east: by(edgeKey(x + 1, y, 'v')) };
};

// Each open tent on a layout: its art, door, and middle in squares.
const openTents = (layout) => Object.entries(layout.openTents || {}).map(([key, { id, facing }]) => {
  const [left, top] = key.split(',').map(Number);
  return { id, turn: doorTurn(facing), x: left + OPEN_TENT_SPAN / 2, y: top + OPEN_TENT_SPAN / 2 };
});

// How a torch sits in an open tent, or null if its square isn't one of the eight around a tent's middle.
const tentTorch = (layout, x, y) => {
  for (const tent of openTents(layout)) {
    const [across, down] = [x + 0.5 - tent.x, y + 0.5 - tent.y];
    if (Math.max(Math.abs(across), Math.abs(down)) !== 1) continue;
    const { size } = WALL_HUGS.torch;
    if (across && down) return { dx: across * TENT_TORCH.outside, dy: down * TENT_TORCH.outside, size };
    const head = (across > 0 && 'west') || (across < 0 && 'east') || (down > 0 && 'north') || 'south';
    return { dx: across * TENT_TORCH.onFabric, dy: down * TENT_TORCH.onFabric, turn: HEAD_TURNS[head], size };
  }
  return null;
};

// How far flush art moves toward the wall on one edge, in squares, so its foot meets the face of the wall as drawn: half
// the wall's thickness out from the edge, less half the art's depth, tucked a little under so the art's soft edges leave
// no floor showing. Null when the art isn't known.
const FLUSH_TUCK = 0.02;
const flushToward = (id, hug, layout, key, art) => {
  const [image] = artList(art, id, false);
  if (!image) return null;
  const kind = layout.walls[key];
  const wall = wallImage(layout, key, kind, art);
  const [longer, shorter] = naturalSize(image).sort((a, b) => b - a);
  return 0.5 - (wall ? wallThickness(wall, 1.04, 1) : WALL_ART[kind].width) / 2 - (hug.size * shorter) / longer / 2 + FLUSH_TUCK;
};

const wallHug = (id, layout, x, y, art = null) => {
  const hug = WALL_HUGS[id];
  if (!hug) return null;
  const inTent = id === 'torch' ? tentTorch(layout, x, y) : null;
  if (inTent) return inTent;
  const { south, west, north, east } = wallSides(layout, x, y);
  const sideways = (Number(east) - Number(west)) * hug.along;
  const upright = (Number(south) - Number(north)) * hug.along;
  const foot = hug.footTurn || 0;
  const key = (south && edgeKey(x, y + 1, 'h')) || (west && edgeKey(x, y, 'v')) || (north && edgeKey(x, y, 'h')) || (east && edgeKey(x + 1, y, 'v'));
  const toward = (key && hug.flush && flushToward(id, hug, layout, key, art)) || hug.toward;
  if (south) return { dx: sideways, dy: toward, turn: foot, size: hug.size };
  if (west) return { dx: -toward, dy: upright, turn: Math.PI / 2 + foot, size: hug.size };
  if (north) return { dx: sideways, dy: -toward, turn: Math.PI + foot, size: hug.size };
  return east ? { dx: toward, dy: upright, turn: -Math.PI / 2 + foot, size: hug.size } : null;
};

// How far to turn art with its top at the head of a bed or stairs, by the way the head points.
const HEAD_TURNS = Object.freeze({ north: 0, south: Math.PI, west: -Math.PI / 2, east: Math.PI / 2 });

// How far to turn an open tent's art, whose door is at the bottom, to put its door the way it faces.
const doorTurn = (facing) => (HEAD_TURNS[facing] + Math.PI) % (Math.PI * 2);

// Touching squares paired up, along the patch's longer side: a square patch runs so an end can reach a wall, and otherwise
// lies down the map. Each entry is a square with the way its pair runs, or no way if it's left on its own.
const pairUp = (cells, layout) => {
  const left = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const free = (x, y) => left.has(cellKey(x, y));
  const xs = cells.map(({ x }) => x);
  const ys = cells.map(({ y }) => y);
  const [wide, tall] = [Math.max(...xs) - Math.min(...xs) + 1, Math.max(...ys) - Math.min(...ys) + 1];
  const sides = (x, y) => wallSides(layout, x, y);
  const endsOnWall = (cell, dir) => (dir === 'across'
    ? sides(cell.x, cell.y).west || sides(cell.x + 1, cell.y).east
    : sides(cell.x, cell.y).north || sides(cell.x, cell.y + 1).south);
  const pairs = [];
  for (const cell of cells) {
    const { x, y } = cell;
    if (!free(x, y)) continue;
    const canAcross = free(x + 1, y);
    const canDown = free(x, y + 1);
    let dir = null;
    if (canAcross && canDown) {
      if (wide !== tall) dir = wide > tall ? 'across' : 'down';
      else dir = endsOnWall(cell, 'across') && !endsOnWall(cell, 'down') ? 'across' : 'down';
    } else if (canAcross) dir = 'across';
    else if (canDown) dir = 'down';
    left.delete(cellKey(x, y));
    if (dir === 'across') left.delete(cellKey(x + 1, y));
    if (dir === 'down') left.delete(cellKey(x, y + 1));
    pairs.push({ cell, dir });
  }
  return pairs;
};

// A patch of beds, in squares. The pillow goes to a wall at either end, or else the top or left.
const bedPieces = (cells, snowyAt, layout) => pairUp(cells, layout).map(({ cell, dir }) => {
  const { x, y } = cell;
  const sides = (a, b) => wallSides(layout, a, b);
  if (!dir) {
    const { south, west, north, east } = sides(x, y);
    const head = (north && 'north') || (south && 'south') || (west && 'west') || (east && 'east') || 'north';
    return { x: x + 0.5, y: y + 0.5, size: BED_SIZE.lone, turn: HEAD_TURNS[head], snowy: snowyAt(cell) };
  }
  if (dir === 'across') {
    const head = !sides(x, y).west && sides(x + 1, y).east ? 'east' : 'west';
    return { x: x + 1, y: y + 0.5, size: BED_SIZE.full, turn: HEAD_TURNS[head], snowy: snowyAt(cell) };
  }
  const head = !sides(x, y).north && sides(x, y + 1).south ? 'south' : 'north';
  return { x: x + 0.5, y: y + 1, size: BED_SIZE.full, turn: HEAD_TURNS[head], snowy: snowyAt(cell) };
});

// A patch of empty tables, in squares: long art, lying along each pair, either way round.
const emptyTablePieces = (cells, snowyAt, layout) => pairUp(cells, layout).map(({ cell, dir }) => {
  const { x, y } = cell;
  const around = Math.floor(wobble(x, y, 27) * 2) * Math.PI;
  if (dir === 'across') return { x: x + 1, y: y + 0.5, size: TABLE_SIZE.empty, turn: around, snowy: snowyAt(cell) };
  if (dir === 'down') return { x: x + 0.5, y: y + 1, size: TABLE_SIZE.empty, turn: Math.PI / 2 + around, snowy: snowyAt(cell) };
  return { x: x + 0.5, y: y + 0.5, size: TABLE_SIZE.lone, turn: around, snowy: snowyAt(cell) };
});

// A patch of filled tables, in squares: one on each full 2x2 not yet used, and a small one on each square left.
const filledTablePieces = (cells, snowyAt) => {
  const left = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const quarter = (x, y) => Math.floor(wobble(x, y, 28) * 4) * (Math.PI / 2);
  const pieces = [];
  for (const cell of cells) {
    const { x, y } = cell;
    const block = [[x, y], [x + 1, y], [x, y + 1], [x + 1, y + 1]].map(([a, b]) => cellKey(a, b));
    if (!block.every((key) => left.has(key))) continue;
    block.forEach((key) => left.delete(key));
    pieces.push({ x: x + 1, y: y + 1, size: TABLE_SIZE.filled, turn: quarter(x, y), diagonal: true, snowy: snowyAt(cell) });
  }
  for (const cell of cells) {
    if (left.has(cellKey(cell.x, cell.y))) pieces.push({ x: cell.x + 0.5, y: cell.y + 0.5, size: TABLE_SIZE.lone, turn: quarter(cell.x, cell.y), snowy: snowyAt(cell) });
  }
  return pieces;
};

// The first square of the trail a footprint is on, corners included, so one boot goes the whole way along it.
const trailStart = (layout, x, y) => {
  const seen = new Set([cellKey(x, y)]);
  for (const key of seen) {
    const [a, b] = key.split(',').map(Number);
    for (const [dx, dy] of AROUND) if (layout.objects[cellKey(a + dx, b + dy)] === 'blood-footprints') seen.add(cellKey(a + dx, b + dy));
  }
  const steps = layout.objectSteps || {};
  return [...seen].sort((a, b) => (steps[a] || 0) - (steps[b] || 0))[0].split(',').map(Number);
};

// A trail of footprints, in squares: each along the way the trail goes, and the last in the worn print of the same boot.
const footprintPieces = (cells, snowyAt, layout) => cells.map((cell) => {
  const { x, y } = cell;
  const { turn, last, bend } = trailStep(layout, x, y);
  const reach = 1 / Math.max(Math.abs(Math.sin(turn)), Math.abs(Math.cos(turn)));
  const { inward, forward, notch } = TRAIL_CORNER;
  const shift = bend ? [bend.dx * inward - Math.sin(turn) * forward, bend.dy * inward + Math.cos(turn) * forward] : [0, 0];
  return { x: x + 0.5 + shift[0], y: y + 0.5 + shift[1], size: bend ? 1 : 1 + (reach - 1) * TRAIL_STRETCH, turn, notch: bend ? notch : null, snowy: snowyAt(cell),
    art: last ? 'blood-footprints-end' : undefined, pick: Math.floor(wobble(...trailStart(layout, x, y), 31) * 1000) };
});

// A patch of closed tents, in squares: one over each center with all eight squares around it, and a small one on each
// square left, so squares that still give cover still show.
const tentPieces = (cells, snowyAt, layout) => {
  const left = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const pieces = [];
  for (const cell of cells) {
    const [x, y] = [cell.x + 1, cell.y + 1];
    const around = AROUND.map(([dx, dy]) => cellKey(x + dx, y + dy));
    if (layout.objects[cellKey(x, y)] !== TENT_CENTER || !around.every((key) => left.has(key))) continue;
    around.forEach((key) => left.delete(key));
    pieces.push({ x: x + 0.5, y: y + 0.5, size: TENT_SIZE.full, turn: Math.floor(wobble(x, y, 29) * 4) * (Math.PI / 2), snowy: snowyAt(cell) });
  }
  for (const cell of cells) {
    if (left.has(cellKey(cell.x, cell.y))) pieces.push({ x: cell.x + 0.5, y: cell.y + 0.5, size: TENT_SIZE.lone, turn: 0, snowy: snowyAt(cell) });
  }
  return pieces;
};

// A patch of stairs, in squares, each with the top of its art the way it goes up. Stairs going the same way join: the edges
// of the art that would show a seam are cut where a flight carries on, and its last step keeps the shadow it was drawn with.
const stairPieces = (cells, snowyAt, layout) => cells.map((cell) => {
  const { x, y } = cell;
  const facing = objectFacing(layout, x, y);
  const { dx, dy } = MAP_FACINGS[facing];
  const joined = (a, b) => layout.objects[cellKey(a, b)] === 'stairs' && objectFacing(layout, a, b) === facing;
  return { x: x + 0.5, y: y + 0.5, size: 1, turn: HEAD_TURNS[facing], snowy: snowyAt(cell), crop: {
    bottom: joined(x - dx, y - dy) ? STAIR_EDGES.shadow : 0,
    right: joined(x - dy, y + dx) ? STAIR_EDGES.side : 0,
    left: joined(x + dy, y - dx) ? STAIR_EDGES.side : 0,
  } };
});

// A patch of crates, in squares: a pile on each full 2x2 not yet used, and a lone crate on each square left. Anything
// beside a wall lines up with it; the rest sit at loose angles.
const cratePieces = (cells, snowyAt, layout) => {
  const onWall = (x, y) => Object.values(wallSides(layout, x, y)).some(Boolean);
  const left = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const pieces = [];
  const angle = (x, y, most) => (wobble(x * 3, y * 3, 5) - 0.5) * 2 * most;
  for (const cell of cells) {
    const { x, y } = cell;
    const block = [[x, y], [x + 1, y], [x, y + 1], [x + 1, y + 1]].map(([a, b]) => cellKey(a, b));
    if (!block.every((key) => left.has(key))) continue;
    block.forEach((key) => left.delete(key));
    const pile = CRATE_PILES[Math.floor(wobble(x, y, 25) * CRATE_PILES.length)];
    const lined = [[x, y], [x + 1, y], [x, y + 1], [x + 1, y + 1]].some(([a, b]) => onWall(a, b));
    for (const [boxLeft, boxTop, width, height] of pile) {
      const [px, py] = [x + boxLeft + width / 2, y + boxTop + height / 2];
      pieces.push({ x: px, y: py, size: 1, box: [width, height], turn: lined ? 0 : angle(px, py, CRATE_ANGLE.piled), snowy: snowyAt(cell) });
    }
  }
  for (const cell of cells) {
    if (!left.has(cellKey(cell.x, cell.y))) continue;
    const { south, west, north, east } = wallSides(layout, cell.x, cell.y);
    const { depth, toward } = CRATE_ON_WALL;
    const upDown = south || north;
    const box = upDown ? [1, depth] : (west || east) ? [depth, 1] : [1, 1];
    const [dx, dy] = upDown ? [0, south ? toward : -toward] : [west ? -toward : east ? toward : 0, 0];
    const turn = upDown || west || east ? 0 : angle(cell.x, cell.y, CRATE_ANGLE.lone);
    pieces.push({ x: cell.x + 0.5 + dx, y: cell.y + 0.5 + dy, size: 1, box, turn, snowy: snowyAt(cell) });
  }
  return pieces;
};

// The pieces of art a patch is made of, in squares: one on each square, then, for blended stamps, one over each join
// between touching squares and one where four squares meet. A line of brush reads as one tangle instead of one image
// repeated, and a solid patch has no holes.
const patchPieces = (group, layout, art = null) => {
  const cells = [...group.cells].sort((a, b) => a.y - b.y || a.x - b.x);
  const snowyAt = ({ x, y }) => SNOWY_GROUND.has(layout.terrain[cellKey(x, y)]);
  if (group.id === 'crates') return cratePieces(cells, snowyAt, layout);
  if (group.id === 'stairs') return stairPieces(cells, snowyAt, layout);
  if (group.id === 'blood-footprints') return footprintPieces(cells, snowyAt, layout);
  if (CLOSED_TENTS.includes(group.id)) return tentPieces(cells, snowyAt, layout);
  if (group.id === 'bed') return bedPieces(cells, snowyAt, layout);
  if (group.id === 'table-empty') return emptyTablePieces(cells, snowyAt, layout);
  if (group.id === 'table-filled') return filledTablePieces(cells, snowyAt);
  const grow = DENSE_STAMPS[group.id]?.size || 1;
  const pieces = cells.map((cell) => {
    if (GROUND_ART.has(group.id)) {
      const span = groundSpan(layout, cell.x, cell.y);
      return { ...span, art: group.id === 'blood-puddle' && span.size > 1 ? 'blood-puddle-large' : undefined, snowy: snowyAt(cell) };
    }
    const hug = wallHug(group.id, layout, cell.x, cell.y, art);
    return { x: cell.x + 0.5 + (hug?.dx || 0), y: cell.y + 0.5 + (hug?.dy || 0), size: WALL_HUGS[group.id]?.size || SMALL_ART[group.id] || LARGE_ART[group.id] || grow, turn: hug?.turn, snowy: snowyAt(cell) };
  });
  if (!BLENDED_STAMPS.has(group.id)) return pieces;
  const inPatch = new Set(cells.map(({ x, y }) => cellKey(x, y)));
  const joinSize = JOIN_SIZE * grow;
  for (const cell of cells) {
    if (inPatch.has(cellKey(cell.x + 1, cell.y))) pieces.push({ x: cell.x + 1, y: cell.y + 0.5, size: joinSize, snowy: snowyAt(cell) });
    if (inPatch.has(cellKey(cell.x, cell.y + 1))) pieces.push({ x: cell.x + 0.5, y: cell.y + 1, size: joinSize, snowy: snowyAt(cell) });
  }
  for (const cell of cells) {
    const meets = [[1, 0], [0, 1], [1, 1]].every(([dx, dy]) => inPatch.has(cellKey(cell.x + dx, cell.y + dy)));
    if (meets) pieces.push({ x: cell.x + 1, y: cell.y + 1, size: joinSize, snowy: snowyAt(cell) });
  }
  const thin = THINNED_STAMPS[group.id];
  if (!thin) return pieces;
  // The same joins go each time, spread over the patch by their place on the map.
  const joins = pieces.slice(cells.length).sort((a, b) => wobble(a.x * 2, a.y * 2, 17) - wobble(b.x * 2, b.y * 2, 17));
  const gone = new Set(joins.slice(0, Math.round(pieces.length * thin)));
  return pieces.filter((piece) => !gone.has(piece));
};

const pieceTurn = (id, x, y) => {
  if (FREE_TURN.has(id)) return wobble(x * 2, y * 2, 5) * Math.PI * 2;
  return QUARTER_TURN.has(id) ? Math.floor(wobble(x * 2, y * 2, 5) * 4) * (Math.PI / 2) : 0;
};

const standsAlone = ({ id, cells }, layout) => cells.length === 1
  && AROUND.every(([dx, dy]) => layout.objects[cellKey(cells[0].x + dx, cells[0].y + dy)] !== id);

const drawPatchArt = (ctx, group, layout, cw, ch, unit, art) => {
  const start = Math.floor(wobble(group.left, group.top, 3) * 1000);
  const lone = LONE_ART[group.id] && standsAlone(group, layout) ? LONE_ART[group.id] : null;
  const pieces = patchPieces(group, layout, art);
  const shade = DENSE_STAMPS[group.id]?.shade;
  // Round, like the art over it, so no square corner shows past the leaves.
  for (const piece of shade ? pieces : []) {
    ctx.fillStyle = shade;
    ctx.beginPath();
    ctx.arc(piece.x * cw, piece.y * ch, piece.size * unit * 0.36, 0, Math.PI * 2);
    ctx.fill();
  }
  pieces.forEach((piece, index) => {
    // Walking through the art in turn means pieces side by side differ wherever the folder has more than one.
    const own = piece.art ? artList(art, piece.art, piece.snowy) : [];
    const list = own.length ? own : [...artList(art, group.id, piece.snowy), ...(lone ? artList(art, lone, piece.snowy) : [])];
    const image = list[(piece.pick ?? start + index) % list.length];
    const [longer, shorter] = naturalSize(image).sort((a, b) => b - a);
    const narrow = piece.diagonal && longer / shorter >= NARROW_TABLE.ratio;
    drawSprite(ctx, image, { cx: piece.x * cw, cy: piece.y * ch, size: unit * (narrow ? NARROW_TABLE.size : piece.size), unit,
      turn: (piece.turn ?? pieceTurn(group.id, piece.x, piece.y)) + (narrow ? Math.PI / 4 : 0), flip: piece.turn === undefined && (FREE_TURN.has(group.id) || QUARTER_TURN.has(group.id)) && wobble(piece.x * 2, piece.y * 2, 7) > 0.5,
      fill: FILLS_SQUARE.has(group.id), shadow: !GROUND_ART.has(group.id) && !piece.crop, box: piece.box?.map((side) => side * unit), crop: piece.crop, notch: piece.notch });
  });
};

// Open tents go under everything else on their squares, as a ring of canvas if the art is missing.
const drawOpenTents = (ctx, layout, cw, ch, unit, art) => {
  for (const { id, turn, x, y } of openTents(layout)) {
    const [image] = artList(art, id, SNOWY_GROUND.has(layout.terrain[cellKey(Math.floor(x), Math.floor(y))]));
    if (image) drawSprite(ctx, image, { cx: x * cw, cy: y * ch, size: unit * TENT_SIZE.full, unit, turn, shadow: false });
    else {
      ctx.strokeStyle = '#8a6a44';
      ctx.lineWidth = unit * 0.2;
      ctx.beginPath();
      ctx.arc(x * cw, y * ch, unit * TENT_WALL.radius, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
};

const drawObjects = (ctx, layout, cw, ch, unit, art) => {
  drawOpenTents(ctx, layout, cw, ch, unit, art);
  const hasArt = (group) => group.cells.every(({ x, y }) => artList(art, group.id, SNOWY_GROUND.has(layout.terrain[cellKey(x, y)])).length);
  const onGround = (group) => GROUND_ART.has(group.id) && hasArt(group);
  // The center of a whole tent is drawn by the art around it.
  const underTent = ({ id, cells }) => id === TENT_CENTER && cells.every(({ x, y }) => {
    const tent = layout.objects[cellKey(x - 1, y - 1)];
    return CLOSED_TENTS.includes(tent) && artList(art, tent, false).length && AROUND.every(([dx, dy]) => layout.objects[cellKey(x + dx, y + dy)] === tent);
  });
  const groups = objectGroups(layout);
  for (const group of groups.filter(onGround)) drawPatchArt(ctx, group, layout, cw, ch, unit, art);
  for (const group of groups.filter((entry) => !onGround(entry) && !underTent(entry))) {
    const withArt = hasArt(group);
    const draw = OBJECT_ART[group.id];
    if (!withArt && !draw) continue;
    const unclipped = withArt && (DENSE_STAMPS[group.id] || LARGE_ART[group.id] || CLOSED_TENTS.includes(group.id));
    ctx.save();
    squareRects(ctx, group.cells, cw, ch);
    if (!unclipped) ctx.clip();
    if (withArt) drawPatchArt(ctx, group, layout, cw, ch, unit, art);
    else {
      for (const { x, y } of group.cells) {
        const hug = wallHug(group.id, layout, x, y);
        draw(ctx, { x, y, x0: x * cw, y0: y * ch, width: cw, height: ch, cx: (x + 0.5 + (hug?.dx || 0)) * cw, cy: (y + 0.5 + (hug?.dy || 0)) * ch, unit, turn: HEAD_TURNS[objectFacing(layout, x, y)],
          snowy: SNOWY_GROUND.has(layout.terrain[cellKey(x, y)]) });
      }
    }
    ctx.restore();
  }
};

// Wall art along one edge, its long side running along the wall, overlapping its neighbours a little to hide the join.
// How thick a piece of wall art lies along an edge this long.
const wallThickness = (image, length, unit) => {
  const [width, height] = naturalSize(image);
  return Math.min(unit * 0.45, length * (Math.min(width, height) / Math.max(width, height)));
};

// The middle of each run of joined walls, in squares, by wall edge. A wall's inside is the side facing that middle, which
// is the room for a closed room or a partly walled one. A lone straight run has no inside.
const wallInsides = (layout) => {
  const ends = (key) => {
    const [x, y, dir] = key.split(',');
    return [[Number(x), Number(y)], dir === 'h' ? [Number(x) + 1, Number(y)] : [Number(x), Number(y) + 1]];
  };
  const touching = new Map();
  const keys = Object.keys(layout.walls);
  for (const key of keys) for (const point of ends(key)) touching.set(String(point), [...(touching.get(String(point)) || []), key]);
  const middles = new Map();
  for (const start of keys) {
    if (middles.has(start)) continue;
    const run = new Set([start]);
    for (const key of run) for (const point of ends(key)) for (const next of touching.get(String(point))) run.add(next);
    const points = [...run].flatMap(ends);
    const middleOf = (axis) => (Math.min(...points.map((point) => point[axis])) + Math.max(...points.map((point) => point[axis]))) / 2;
    for (const key of run) middles.set(key, [middleOf(0), middleOf(1)]);
  }
  return middles;
};

// Wall art lies with its bottom to the south, or to the west on an upright wall, so it turns over when the inside is the
// other way. That keeps a shaded side, where the art has one, facing into the room all the way round.
const facesAway = (key, middle) => {
  const [x, y, dir] = key.split(',');
  return dir === 'h' ? middle[1] < Number(y) : middle[0] > Number(x);
};

const drawWallArt = (ctx, image, kind, x, y, dir, cw, ch, unit, turnOver = false) => {
  const [width, height] = naturalSize(image);
  const length = (dir === 'h' ? cw : ch) * 1.04;
  const across = wallThickness(image, length, unit);
  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = unit * 0.2;
  ctx.translate(dir === 'h' ? x + cw / 2 : x, dir === 'v' ? y + ch / 2 : y);
  ctx.rotate((dir === 'v' ? Math.PI / 2 : 0) + (height > width ? Math.PI / 2 : 0));
  if (wobble(x, y, 9) > 0.5 && kind !== 'door') ctx.scale(-1, 1);
  if (turnOver && width >= height) ctx.scale(1, -1);
  const [drawWidth, drawHeight] = height > width ? [across, length] : [length, across];
  ctx.drawImage(image, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight);
  ctx.restore();
};

// Forgotten Adventures art is drawn at this many pixels to a square, which keeps corners the same width as their walls.
const ART_PIXELS_PER_SQUARE = 200;
// How to turn corner art, drawn with its arms running right and down, by the two ways the walls leave the corner.
const CORNER_TURNS = Object.freeze({ 'down,right': 0, 'down,left': Math.PI / 2, 'left,up': Math.PI, 'right,up': -Math.PI / 2 });

// Corner art over the walls, at its own size, where exactly two full walls of one set meet at a right angle. Its outer
// edges line up with the outer edges of the walls.
const drawWallCorners = (ctx, layout, cw, ch, unit, art) => {
  const styles = layout.wallStyles || {};
  const points = new Set(Object.keys(styles).flatMap((key) => {
    const [x, y, dir] = key.split(',');
    return [`${x},${y}`, dir === 'h' ? `${Number(x) + 1},${y}` : `${x},${Number(y) + 1}`];
  }));
  for (const point of points) {
    const [x, y] = point.split(',').map(Number);
    const arms = { right: `${x},${y},h`, left: `${x - 1},${y},h`, down: `${x},${y},v`, up: `${x},${y - 1},v` };
    const meeting = Object.keys(arms).filter((side) => layout.walls[arms[side]]).sort();
    const turn = CORNER_TURNS[meeting.join()];
    const keys = meeting.map((side) => arms[side]);
    if (turn === undefined || !keys.every((key) => layout.walls[key] === 'wall') || styles[keys[0]] !== styles[keys[1]]) continue;
    const set = art?.['wall-sets']?.[styles[keys[0]]];
    if (!set?.corner?.plain?.length || !set.wall?.plain?.length) continue;
    const image = set.corner.plain[Math.floor(wobble(x, y, 6) * set.corner.plain.length)];
    const scale = unit / ART_PIXELS_PER_SQUARE;
    const inset = (Math.min(...naturalSize(set.wall.plain[0])) * scale) / 2;
    const [width, height] = naturalSize(image);
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = unit * 0.2;
    ctx.translate(x * cw, y * ch);
    ctx.rotate(turn);
    ctx.drawImage(image, -inset, -inset, width * scale, height * scale);
    ctx.restore();
  }
};

// The art drawn for the wall on one edge, from its set or the plain wall art, or null if it is drawn in code.
const wallImage = (layout, key, kind, art) => {
  const [xs, ys] = key.split(',').map(Number);
  const fromSet = art?.['wall-sets']?.[layout.wallStyles?.[key]]?.[kind]?.plain;
  const images = fromSet?.length ? fromSet : artList(art, kind, false);
  return images.length ? images[Math.floor(wobble(xs, ys, 4) * images.length)] : null;
};

const drawWalls = (ctx, layout, cw, ch, unit, art) => {
  // Walls first, then openings, so a door's bar sits over the ends of the walls beside it.
  const order = { wall: 0, 'low-wall': 1, window: 2, door: 3 };
  const edges = Object.entries(layout.walls).filter(([, kind]) => WALL_KINDS[kind]).sort(([, a], [, b]) => order[a] - order[b]);
  const insides = wallInsides(layout);
  for (const [key, kind] of edges) {
    const [xs, ys, dir] = key.split(',');
    const x = Number(xs) * cw;
    const y = Number(ys) * ch;
    const image = wallImage(layout, key, kind, art);
    if (image) {
      drawWallArt(ctx, image, kind, x, y, dir, cw, ch, unit, facesAway(key, insides.get(key)));
      continue;
    }
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
    ctx.shadowBlur = unit * 0.2;
    ctx.strokeStyle = WALL_ART[kind].color;
    ctx.lineWidth = unit * WALL_ART[kind].width;
    ctx.lineCap = kind === 'door' ? 'butt' : 'round';
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(dir === 'h' ? x + cw : x, dir === 'v' ? y + ch : y);
    ctx.stroke();
    ctx.restore();
  }
  drawWallCorners(ctx, layout, cw, ch, unit, art);
};

/**
 * The tree tops for a layout, as shares of the map in percent, so they follow it at any size. Trees on snow are bare.
 * One layout always gives the same tops.
 */
export const treeTopPieces = (layout, catalog = MAP_ART) => {
  const tops = catalog['tree-top']?.plain || [];
  if (!tops.length || !layout?.objects) return [];
  return objectGroups(layout).filter(({ id }) => id === 'tree').flatMap(({ cells }) => cells
    .filter(({ x, y }) => !SNOWY_GROUND.has(layout.terrain[cellKey(x, y)]))
    .map(({ x, y }) => ({
      key: cellKey(x, y),
      url: tops[Math.floor(wobble(x, y, 11) * tops.length)],
      left: ((x + 0.5) / layout.columns) * 100,
      top: ((y + 0.5) / layout.rows) * 100,
      width: (TREE_TOP_SQUARES / layout.columns) * 100,
      height: (TREE_TOP_SQUARES / layout.rows) * 100,
      turn: wobble(x, y, 13) * 360,
      flip: wobble(x, y, 15) > 0.5,
    })));
};

/**
 * A layout's stickers as they lie on the map (MapStickers): each one's middle and size as shares of the map, its turn in
 * degrees, and its picture, or no `url` when the catalog has none. They're never painted into the map image.
 */
export const stickerPieces = (layout, catalog = MAP_ART) => (layout?.stickers || []).filter(({ kind }) => STICKERS[kind]).map((sticker) => {
  const pictures = catalog[stickerArtId(sticker.kind)]?.plain || [];
  const { label, width, length, fit } = STICKERS[sticker.kind];
  return {
    key: sticker.id,
    kind: sticker.kind,
    label,
    url: pictures.length ? pictures[sticker.art % pictures.length] : null,
    left: (sticker.x / layout.columns) * 100,
    top: (sticker.y / layout.rows) * 100,
    width: (width / layout.columns) * 100,
    height: (length / layout.rows) * 100,
    turn: sticker.turn,
    fit: fit || null,
  };
});

/**
 * Draws a layout's shade, roofs, stamps and walls, in that order, on a transparent or ground-filled 2D canvas context.
 * `art` is what loadMapArt returned; anything it lacks is drawn in code.
 */
// A wall saved with no set, or one no longer in the art, draws with the first set there is if it would otherwise fall to
// the drawn stand-in.
const withWallSets = (layout, art) => {
  const sets = art?.['wall-sets'] || {};
  const [first] = Object.keys(sets);
  const bare = ([key, kind]) => !sets[layout.wallStyles?.[key]] && !artList(art, kind, false).length;
  const needing = Object.entries(layout.walls).filter(bare);
  if (!first || !needing.length) return layout;
  return { ...layout, wallStyles: { ...layout.wallStyles, ...Object.fromEntries(needing.map(([key]) => [key, first])) } };
};

export const drawLayoutOverlay = (ctx, plainLayout, width, height, art = null) => {
  const layout = withWallSets(plainLayout, art);
  const cw = width / layout.columns;
  const ch = height / layout.rows;
  const unit = Math.min(cw, ch);
  drawShade(ctx, layout, cw, ch, unit);
  drawRoofs(ctx, layout, cw, ch, unit, art);
  drawObjects(ctx, layout, cw, ch, unit, art);
  drawWalls(ctx, layout, cw, ch, unit, art);
};

const loadImage = (source) => new Promise((resolve, reject) => {
  const image = new Image();
  // A ground from a URL must allow reading back, or the finished map couldn't be saved.
  if (!String(source).startsWith('data:')) image.crossOrigin = 'anonymous';
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('The painted ground could not be loaded.'));
  image.src = source;
});

// Each image loads once a session. One that fails is left out, so its stamp falls back to the art drawn in code.
const loadedArt = new Map();
const artImage = (url) => {
  if (!loadedArt.has(url)) loadedArt.set(url, loadImage(url).catch(() => null));
  return loadedArt.get(url);
};
const artImages = async (urls = []) => (await Promise.all(urls.map(artImage))).filter(Boolean);

/** The images a layout needs from the catalog, ready to draw: stamp, wall or roof id -> plain and snow images. */
export const loadMapArt = async (layout, catalog = MAP_ART) => {
  const ids = new Set([...Object.values(layout.objects), ...Object.values(layout.walls), ...openTents(layout).map(({ id }) => id)]);
  if (Object.values(layout.terrain).includes('roof')) ids.add('roof');
  for (const [id, lone] of Object.entries({ ...LONE_ART, ...ART_STAND_INS, ...ART_WITH })) if (ids.has(id)) ids.add(lone);
  const loaded = await Promise.all([...ids].filter((id) => catalog[id]).map(async (id) => [id,
    { plain: await artImages(catalog[id].plain), snow: await artImages(catalog[id].snow) }]));
  const sets = catalog['wall-sets'] || {};
  const styled = [...new Set(Object.values(layout.wallStyles || {}))].filter((style) => sets[style]);
  // Walls saved with no set still use one.
  const used = styled.length || !Object.keys(layout.walls).length ? styled : Object.keys(sets).slice(0, 1);
  const wallSets = await Promise.all(used.map(async (style) => [style, Object.fromEntries(await Promise.all(Object.entries(sets[style])
    .map(async ([kind, urls]) => [kind, { plain: await artImages(urls.plain), snow: [] }])))]));
  return { ...Object.fromEntries(loaded), ...(wallSets.length ? { 'wall-sets': Object.fromEntries(wallSets) } : {}) };
};

const newCanvas = (width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot draw the map.');
  return { canvas, ctx };
};

/**
 * The shadows a light casts: for each wall edge or stamp in its reach that stops any of it, how much it stops and the
 * area behind it, away from the light. A stamp casts from its widest line across, as the light sees it.
 */
export const lightShadows = ({ cx, cy, radius }, layout, width, height) => {
  const cw = width / layout.columns;
  const ch = height / layout.rows;
  const unit = Math.min(cw, ch);
  const inReach = (x, y) => Math.hypot(x - cx, y - cy) < radius + unit;
  const behind = ([x, y]) => {
    const away = (radius * 2) / Math.hypot(x - cx, y - cy);
    return [x + (x - cx) * away, y + (y - cy) * away];
  };
  const shadow = (start, end, block) => ({ block, points: [start, end, behind(end), behind(start)] });
  const fromWalls = Object.entries(layout.walls).flatMap(([key, kind]) => {
    const [xs, ys, dir] = key.split(',');
    const [x, y] = [Number(xs) * cw, Number(ys) * ch];
    if (!WALL_LIGHT_BLOCKS[kind] || !inReach(dir === 'h' ? x + cw / 2 : x, dir === 'h' ? y : y + ch / 2)) return [];
    // A little past each end, so the soft edges of neighbouring edges overlap and no light leaks between them.
    const lap = unit * 0.15;
    return [dir === 'h' ? shadow([x - lap, y], [x + cw + lap, y], WALL_LIGHT_BLOCKS[kind])
      : shadow([x, y - lap], [x, y + ch + lap], WALL_LIGHT_BLOCKS[kind])];
  });
  const fromStamps = Object.entries(layout.objects).flatMap(([key, id]) => {
    const [xs, ys] = key.split(',').map(Number);
    const [x, y] = [(xs + 0.5) * cw, (ys + 0.5) * ch];
    const away = Math.hypot(x - cx, y - cy);
    if (!LIGHT_BLOCKS[id] || !away || !inReach(x, y)) return [];
    const half = (LIGHT_BLOCKS[id].size * unit) / 2;
    const across = ([px, py]) => (px * (cy - y) + py * (x - cx)) / away;
    const corners = [[x - half, y - half], [x + half, y - half], [x + half, y + half], [x - half, y + half]].sort((a, b) => across(a) - across(b));
    return [shadow(corners[0], corners[3], LIGHT_BLOCKS[id].block)];
  });
  // An open tent's fabric all the way round from one side of its door to the other, so light only passes through the door.
  const fromTents = openTents(layout).flatMap(({ x, y, turn }) => {
    if (Math.hypot(x * cw - cx, y * ch - cy) >= radius + unit * OPEN_TENT_SPAN) return [];
    const { radius: out, pieces, doorAngle, doorWidth } = TENT_WALL;
    const from = doorAngle + turn + doorWidth / 2;
    const step = (Math.PI * 2 - doorWidth) / pieces;
    const at = (angle) => [(x + Math.cos(angle) * out) * cw, (y + Math.sin(angle) * out) * ch];
    // Lapped as walls are, except at the door.
    const lap = step * 0.3;
    return Array.from({ length: pieces }, (_, index) => shadow(at(from + index * step - (index ? lap : 0)), at(from + (index + 1) * step + (index < pieces - 1 ? lap : 0)), 1));
  });
  return [...fromWalls, ...fromStamps, ...fromTents];
};

/**
 * The players' fog texture: the smoke art laid over `clouds`, the generated fog, as opaque pixels. It's built only from
 * those two, never the map, so hidden squares show nothing of what's under them. Null if the art is missing or unreadable.
 */
export const smokeFogTexture = async (size, clouds, catalog = MAP_ART) => {
  const smoke = await artImages(catalog.fog?.plain);
  if (!smoke.length) return null;
  const { ctx } = newCanvas(size, size);
  const base = ctx.createImageData(size, size);
  base.data.set(clouds);
  ctx.putImageData(base, 0, 0);
  const step = size / SMOKE_ROWS;
  const across = step * 2;
  for (let y = -1; y <= SMOKE_ROWS; y += 1) {
    for (let x = -1; x <= SMOKE_ROWS; x += 1) {
      ctx.save();
      ctx.globalAlpha = 0.55;
      ctx.translate((x + 0.5 + wobble(x, y, 21) * 0.6) * step, (y + 0.5 + wobble(x, y, 22) * 0.6) * step);
      ctx.rotate(wobble(x, y, 23) * Math.PI * 2);
      ctx.drawImage(smoke[Math.floor(wobble(x, y, 24) * smoke.length)], -across / 2, -across / 2, across, across);
      ctx.restore();
    }
  }
  // A cool shade over the light smoke, so the fog stays dark and moody.
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = '#7f8ca0';
  ctx.fillRect(0, 0, size, size);
  try {
    const { data } = ctx.getImageData(0, 0, size, size);
    for (let i = 3; i < data.length; i += 4) data[i] = 255;
    return data;
  } catch {
    return null;
  }
};

export const lightPools = (layout, width, height, art = null) => {
  const cw = width / layout.columns;
  const ch = height / layout.rows;
  const drawn = withWallSets(layout, art);
  return objectGroups(layout).filter(({ id }) => LIGHT_STAMPS[id]).flatMap(({ id, cells }) => cells.map(({ x, y }) => {
    const light = LIGHT_STAMPS[id];
    // A torch in a tent and a wall light shine from where they are drawn, not their square's middle.
    const moved = id === 'torch' ? tentTorch(layout, x, y) : id === 'wall-light' ? wallHug(id, drawn, x, y, art) : null;
    const span = GROUND_ART.has(id) ? groundSpan(layout, x, y) : { size: 1, x: x + 0.5 + (moved?.dx || 0), y: y + 0.5 + (moved?.dy || 0) };
    const reach = light.reachPast ? span.size / 2 + light.reachPast : light.reach;
    const pool = { ...light, cx: span.x * cw, cy: span.y * ch, radius: Math.min(cw, ch) * reach, ring: span.size / 2 / reach };
    return { ...pool, shadows: lightShadows(pool, layout, width, height) };
  }));
};

const LIFT_STOPS = [[0, 'rgba(0, 0, 0, 1)'], [0.4, 'rgba(0, 0, 0, 0.8)'], [1, 'rgba(0, 0, 0, 0)']];
// A colored glow is strongest at the circle's ring, then fades out over the squares beyond it.
const glowStops = ({ strength, color, ring }) => {
  const rgb = color?.join(', ');
  return color
    ? [[0, `rgba(${rgb}, 0.3)`], [ring, `rgba(${rgb}, 0.42)`], [ring + (1 - ring) * 0.33, `rgba(${rgb}, 0.2)`], [1, `rgba(${rgb}, 0)`]]
    : [[0, `rgba(255, 176, 96, ${0.6 * strength})`], [0.4, `rgba(255, 150, 72, ${0.3 * strength})`], [1, 'rgba(255, 130, 60, 0)']];
};

// One light on its own layer: its pool, with soft-edged shadows cut out where something stands in the way.
const litLayer = ({ canvas, ctx }, pool, stops, width, height) => {
  const { cx, cy, radius } = pool;
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, width, height);
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  for (const [at, color] of stops) gradient.addColorStop(at, color);
  ctx.fillStyle = gradient;
  ctx.fillRect(cx - radius, cy - radius, radius * 2, radius * 2);
  ctx.globalCompositeOperation = 'destination-out';
  ctx.filter = `blur(${radius * 0.02}px)`;
  for (const { block, points } of pool.shadows) {
    ctx.fillStyle = `rgba(0, 0, 0, ${block})`;
    ctx.beginPath();
    points.forEach(([x, y], index) => (index ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
    ctx.closePath();
    ctx.fill();
  }
  ctx.filter = 'none';
  return canvas;
};

// The night tint for placed art, lifted around each light so the fire and what's beside it aren't dimmed.
const nightTint = (width, height, pools, layer) => {
  const tint = newCanvas(width, height);
  tint.ctx.fillStyle = NIGHT_TINT;
  tint.ctx.fillRect(0, 0, width, height);
  tint.ctx.globalCompositeOperation = 'destination-out';
  for (const pool of pools) tint.ctx.drawImage(litLayer(layer, pool, LIFT_STOPS, width, height), 0, 0);
  return tint.canvas;
};

/**
 * A painted ground with the layout drawn over it, as a PNG data URL. At night the placed art is dimmed to match, and
 * light stamps brighten the ground around them and lift the dimming from the art there.
 */
export const composeMapImage = async (groundSource, layout, catalog = MAP_ART) => {
  if (!groundSource) throw new Error('No painted ground came back.');
  const [ground, images] = await Promise.all([loadImage(groundSource), loadMapArt(layout, catalog)]);
  const groundWidth = ground.naturalWidth || ground.width;
  const groundHeight = ground.naturalHeight || ground.height;
  const scale = Math.max(1, MAP_OUTPUT.side / Math.max(groundWidth, groundHeight));
  const width = Math.round(groundWidth * scale);
  const height = Math.round(groundHeight * scale);
  const art = newCanvas(width, height);
  art.ctx.imageSmoothingQuality = 'high';
  drawLayoutOverlay(art.ctx, layout, width, height, images);
  const pools = lightPools(layout, width, height, images).filter(({ always }) => always || layout.lighting === 'night');
  const layer = pools.length ? newCanvas(width, height) : null;
  if (layout.lighting === 'night') {
    art.ctx.globalCompositeOperation = 'source-atop';
    if (pools.length) art.ctx.drawImage(nightTint(width, height, pools, layer), 0, 0);
    else {
      art.ctx.fillStyle = NIGHT_TINT;
      art.ctx.fillRect(0, 0, width, height);
    }
  }
  const map = newCanvas(width, height);
  map.ctx.imageSmoothingQuality = 'high';
  map.ctx.drawImage(ground, 0, 0, width, height);
  // The glow goes on the ground, under the art, so it doesn't wash out the fire itself.
  map.ctx.globalCompositeOperation = 'screen';
  for (const pool of pools) map.ctx.drawImage(litLayer(layer, pool, glowStops(pool), width, height), 0, 0);
  map.ctx.globalCompositeOperation = 'source-over';
  map.ctx.drawImage(art.canvas, 0, 0);
  return map.canvas.toDataURL(MAP_OUTPUT.type, MAP_OUTPUT.quality);
};
